import type { PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import type { Dayjs } from 'dayjs';
import type { DateRange, DateRanges } from './types';

import { createContext, memo, useContext } from 'react';

import { Box } from '@mui/material';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';

import {
  getRangeAvailabilityDayDisabledOverrideSx,
  getRangeAvailabilityDayVariantSx,
} from './range-availability-calendar-styles';
import { getRangeAvailabilityDayMeta } from './utils';

type RangeAvailabilityCalendarContextValue = {
  window: DateRange;
  preferred?: DateRanges;
  excluded?: DateRanges;
  selectedDates: Dayjs[];
};

const RangeAvailabilityCalendarContext =
  createContext<RangeAvailabilityCalendarContextValue | null>(null);

export const RangeAvailabilityCalendarProvider = RangeAvailabilityCalendarContext.Provider;

const useRangeAvailabilityCalendarContext = () => {
  const context = useContext(RangeAvailabilityCalendarContext);

  if (!context) {
    throw new Error('RangeAvailabilityCalendarDay must be used within RangeAvailabilityCalendarMonth');
  }

  return context;
};

export const RangeAvailabilityCalendarDay = memo((props: PickersDayProps<Dayjs>) => {
  const { sx, disabled, day, outsideCurrentMonth, ...other } = props;
  const { window, preferred, excluded, selectedDates } = useRangeAvailabilityCalendarContext();

  if (outsideCurrentMonth) {
    return <Box sx={{ width: 36, height: 36, flexShrink: 0 }} aria-hidden />;
  }

  const dayMeta = getRangeAvailabilityDayMeta(day, {
    window,
    preferred,
    excluded,
    selectedDates,
  });
  const { variant, showPreferredIndicator, isSelectable } = dayMeta;

  return (
    <Box sx={{ position: 'relative', width: 36, height: 36, flexShrink: 0 }}>
      <PickersDay
        {...other}
        day={day}
        outsideCurrentMonth={false}
        disableMargin
        disabled={disabled ?? !isSelectable}
        selected={false}
        sx={[
          (theme) => ({
            width: 36,
            height: 36,
            m: 0,
            borderRadius: 0.5,
            typography: 'body2',
            ...getRangeAvailabilityDayVariantSx(theme, variant),
          }),
          (theme) => getRangeAvailabilityDayDisabledOverrideSx(theme, variant) ?? {},
          ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ]}
      />
      {showPreferredIndicator ? (
        <Box
          sx={{
            position: 'absolute',
            top: 4,
            right: 4,
            width: 6,
            height: 6,
            borderRadius: '50%',
            bgcolor: 'success.main',
            pointerEvents: 'none',
          }}
        />
      ) : null}
    </Box>
  );
});

RangeAvailabilityCalendarDay.displayName = 'RangeAvailabilityCalendarDay';
